import React from "react";
import { useApi } from "@tiny/api";
import {
  useGetRetentionIntegrationQuery,
  usePutSegmentBySegmentIdMutation,
} from "@tiny/api";
import { useMessages } from "@tiny/components";
import useBreakpoints from "@tiny/components/src/hooks/useBreakpoints";
import { useRefCallBack } from "@tiny/components/src/hooks/useRefCallBack";
import { AudienceSourceDetailsHeaderProps } from "./AudienceSourceDetailsHeader";

export function useAudienceSourceDetailsHeader(
  props: AudienceSourceDetailsHeaderProps
) {
  const api = useApi();
  const { title, segmentId, segmentType } = props;

  const { mdUp } = useBreakpoints();

  const { showWarningMessage, closeMessage, showErrorMessage } = useMessages();

  const [editing, setEditing] = React.useState(false);
  const [internalTitle, setInternalTitle] = React.useState(title);
  const messageIdRef = React.useRef<string | undefined>();

  const { mutateAsync: putSegmentMutateAsync, isLoading: isPutSegmentLoading } =
    usePutSegmentBySegmentIdMutation({
      onError() {
        return undefined;
      },
    });

  const { data: retentionIntegration } = useGetRetentionIntegrationQuery();

  const titleEditable = React.useMemo(
    () =>
      !["PREDEFINED", "INTEGRATION"].includes(segmentType as string) &&
      retentionIntegration?.segmentId !== segmentId &&
      retentionIntegration?.reactivationSegmentId !== segmentId,
    [
      segmentType,
      retentionIntegration?.segmentId,
      retentionIntegration?.reactivationSegmentId,
      segmentId,
    ]
  );

  const editTitle = () => setEditing(true);

  const clearMessage = React.useCallback(async () => {
    if (messageIdRef.current) {
      await closeMessage(messageIdRef.current);
      messageIdRef.current = undefined;
    }
  }, [closeMessage]);

  const saveTitle = useRefCallBack(
    React.useCallback(
      async (editingName: string) => {
        clearMessage();
        if (editingName && editingName !== title) {
          try {
            const { data: nameIsUsed } = await api.putSegmentCheckExists({
              name: editingName,
              type: segmentType,
            });
            if (nameIsUsed) {
              messageIdRef.current = showWarningMessage(
                `Audience with name "${editingName}" already exists!`
              );
            } else {
              await putSegmentMutateAsync({
                segmentId,
                data: { name: editingName },
              });
              setInternalTitle(editingName);
            }
          } catch (e) {
            showErrorMessage("Something wrong happened");
          } finally {
            setEditing(false);
          }
        } else {
          setEditing(false);
        }
      },
      [
        clearMessage,
        putSegmentMutateAsync,
        segmentId,
        segmentType,
        showErrorMessage,
        showWarningMessage,
        title,
      ]
    )
  );

  const cancelEdit = React.useCallback(() => {
    setEditing(false);
    clearMessage();
  }, [clearMessage]);

  React.useEffect(() => {
    setInternalTitle(title);
  }, [title]);

  const saving = isPutSegmentLoading;

  const [cleanedHistoryDialogOpen, setCleanedHistoryDialogOpen] =
    React.useState(false);
  const [productDetailsDialogOpen, setProductDetailsDialogOpen] =
    React.useState(false);

  return {
    editTitle,
    editing,
    cancelEdit,
    internalTitle,
    saveTitle,
    saving,
    mdUp,
    titleEditable,
    onCleanList: () => setProductDetailsDialogOpen(true),
    onDownloadCleanedList: () => setCleanedHistoryDialogOpen(true),
    cleanedHistoryDialogOpen,
    closeCleanedHistoryDialog: () => setCleanedHistoryDialogOpen(false),
    productDetailsDialogOpen,
    closeProductDetailsDialog: () => setProductDetailsDialogOpen(false),
  };
}
